import React, { useState } from 'react';
import { FaSearch, FaFilter, FaChevronLeft, FaChevronRight, FaInbox } from 'react-icons/fa';
import './DataTable.css';

const DataTable = ({
    title,
    columns,
    data,
    onSearch,
    filterOptions = [],
    actions = null,
    leftControls = null,
    onRowClick = null
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    const filteredData = data.filter(item => {
        const matchesSearch = Object.values(item).some(val =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
        );
        const matchesFilter = activeFilter === 'all' || Object.values(item).includes(activeFilter);
        return matchesSearch && matchesFilter;
    });

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const displayedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="premium-data-table-container">
            {/* Table Heading */}
            <div className="table-top-section">
                <div className="table-header-info">
                    <h3 className="table-title">{title}</h3>
                    <p className="table-count">{filteredData.length} records total</p>
                </div>

                <div className="table-controls">
                    {leftControls && <div className="table-left-controls">{leftControls}</div>}
                    <div className="search-box">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search records..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                    </div>

                    {filterOptions.length > 0 && (
                        <div className="filter-box">
                            <FaFilter className="filter-icon" />
                            <select
                                value={activeFilter}
                                onChange={(e) => { setActiveFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="all">All Filters</option>
                                {filterOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {actions && <div className="table-actions">{actions}</div>}
                </div>
            </div>

            {/* Table Structure */}
            <div className="table-wrapper">
                <table className="premium-table">
                    <thead>
                        <tr>
                            {columns.map((col, idx) => (
                                <th key={idx} style={{ width: col.width }}>{col.header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayedData.length > 0 ? (
                            displayedData.map((row, rowIdx) => (
                                <tr
                                    key={rowIdx}
                                    onClick={() => onRowClick && onRowClick(row)}
                                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                >
                                    {columns.map((col, colIdx) => (
                                        <td key={colIdx}>
                                            {col.render ? col.render(row) : row[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="no-data-cell">
                                    <div className="no-data-container">
                                        <FaInbox className="no-data-icon" />
                                        <h4>No Data Available</h4>
                                        <p>We couldn't find any records matching your current search or filters.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="table-pagination">
                    <span className="pagination-info">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length}
                    </span>
                    <div className="pagination-btns">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable;
